import {
  createContext,
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback
} from 'react';
import { useAuth } from 'react-oidc-context';
import { useTenant } from './TenantContext';
import { listsService } from '../services/listsService';

const ListFilterContext = createContext({
  selectedListId: null,
  setSelectedListId: () => {},
  activeLists: [],
  isLoadingLists: false,
  refreshLists: () => {},
  selectedList: null,
});

function ListFilterProvider({ children }) {
  const [selectedListId, setSelectedListId] = useState(null);
  const [activeLists, setActiveLists] = useState([]);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const auth = useAuth();
  const tenant = useTenant();

  // Load active lists
  const loadActiveLists = useCallback(async () => {
    if (!auth.user?.id_token || !tenant.tenant) {
      return;
    }

    setIsLoadingLists(true);
    try {
      const response = await listsService.getActiveLists(auth.user.id_token, tenant.tenant);
      const lists = response.lists || [];
      setActiveLists(lists);
    } catch (error) {
      console.error('ListFilterContext: Error loading active lists:', error);
    } finally {
      setIsLoadingLists(false);
    }
  }, [auth.user?.id_token, tenant.tenant]);

  // Load lists on mount and when auth/tenant changes
  useEffect(() => {
    // Wait for tenant to finish loading before trying to load lists
    if (tenant.isLoading) {
      return;
    }
    loadActiveLists();
  }, [loadActiveLists, tenant.isLoading, auth.user?.id_token, tenant.tenant]);

  // Clear selected list when tenant changes (list may not exist in new tenant)
  useEffect(() => {
    setSelectedListId(null);
  }, [tenant.tenant]);

  // Get the selected list object
  const selectedList = useMemo(() => {
    if (!selectedListId) return null;
    return activeLists.find(l => l.list_id === selectedListId) || null;
  }, [selectedListId, activeLists]);

  const context = useMemo(() => ({
    selectedListId,
    setSelectedListId,
    activeLists,
    isLoadingLists,
    refreshLists: loadActiveLists,
    selectedList,
  }), [
    selectedListId,
    activeLists,
    isLoadingLists,
    loadActiveLists,
    selectedList,
  ]);

  return (
    <ListFilterContext.Provider value={context}>
      {children}
    </ListFilterContext.Provider>
  );
}

export const useListFilter = () => {
  return useContext(ListFilterContext);
};

export { ListFilterProvider, ListFilterContext };
